import React, { useEffect, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Card,
    CardContent,
    IconButton,
    Stack,
    Typography,
    Box,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TodayIcon from '@mui/icons-material/Today';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import { DetalleDia } from './DetalleDia';
import { TarjetaTarea } from './TarjetaTarea';
import { formatearIdDia, obtenerEventosDia } from './calendarioUtils';

const getWeekDays = (referenceDate) => {
    const base = new Date(referenceDate);
    const diff = base.getDay() === 0 ? 6 : base.getDay() - 1;
    const monday = new Date(base);
    monday.setDate(base.getDate() - diff);

    return Array.from({ length: 7 }, (_, index) => {
        const day = new Date(monday);
        day.setDate(monday.getDate() + index);
        return day;
    });
};

export const CalendarioMovil = ({
    referenceDate,
    fechaSeleccionada,
    onExpandDay,
    onCreateDay,
    onPrevWeek,
    onNextWeek,
    onToday,
    tareas,
    onChanged,
    mostrarFinesSemana = false,
}) => {
    const weekDays = getWeekDays(referenceDate).filter(
        (dayValue) => mostrarFinesSemana || (dayValue.getDay() !== 0 && dayValue.getDay() !== 6)
    );
    const [expandedDay, setExpandedDay] = useState(fechaSeleccionada || null);

    useEffect(() => {
        setExpandedDay(fechaSeleccionada || null);
    }, [fechaSeleccionada, referenceDate]);

    return (
        <Stack spacing={2}>
            <Card sx={{ borderRadius: '8px' }}>
                <CardContent sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <IconButton onClick={onPrevWeek}>
                            <ArrowBackIosNewIcon fontSize="small" />
                        </IconButton>

                        <Stack direction="row" alignItems="center" spacing={1}>
                            <TodayIcon sx={{ fontSize: 18, color: 'var(--principal)' }} />
                            <Typography sx={{ fontWeight: 700 }}>
                                Semana
                            </Typography>
                        </Stack>

                        <IconButton onClick={onNextWeek}>
                            <ArrowForwardIosIcon fontSize="small" />
                        </IconButton>
                    </Stack>

                    <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                        <IconButton onClick={onToday}>
                            <TodayIcon />
                        </IconButton>
                    </Stack>
                </CardContent>
            </Card>

            {weekDays.map((dayValue) => {
                const dayId = formatearIdDia(dayValue);
                const eventosDia = obtenerEventosDia(tareas, dayId);
                const expanded = expandedDay === dayId;
                const isToday = dayId === formatearIdDia(new Date());

                return (
                    <Accordion
                        key={dayId}
                        expanded={expanded}
                        onChange={(_, nextExpanded) => {
                            if (nextExpanded) {
                                setExpandedDay(dayId);
                                onExpandDay(dayId);
                                return;
                            }

                            setExpandedDay(null);
                        }}
                        disableGutters
                        sx={{
                            borderRadius: '8px !important',
                            overflow: 'hidden',
                            border: isToday ? '2px solid var(--principal)' : '1px solid rgba(0,0,0,0.12)',
                            boxShadow: isToday ? '0 10px 22px rgba(229, 57, 53, 0.12)' : 'none',
                            '&:before': { display: 'none' },
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                backgroundColor: isToday ? 'rgba(229, 57, 53, 0.16)' : '#fff',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    width: '100%',
                                    pr: 1,
                                    gap: 1,
                                }}
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    <Stack direction="row" spacing={0.8} alignItems="center">
                                        <Typography sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                                            {dayValue.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric' })}
                                        </Typography>
                                        {isToday && (
                                            <Box
                                                sx={{
                                                    px: 0.75,
                                                    py: 0.15,
                                                    borderRadius: 999,
                                                    backgroundColor: '#fff',
                                                    color: 'var(--principal)',
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    lineHeight: 1.5,
                                                }}
                                            >
                                                Hoy
                                            </Box>
                                        )}
                                    </Stack>
                                    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                                        {eventosDia.length} {eventosDia.length === 1 ? 'tarea' : 'tareas'}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--principal)' }}>
                                        {eventosDia.length > 0 ? `${eventosDia.length} previstas` : 'Libre'}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            onCreateDay(dayId);
                                        }}
                                        sx={{ color: 'var(--principal)' }}
                                    >
                                        <AddIcon fontSize="small" />
                                    </IconButton>
                                </Stack>
                            </Box>
                        </AccordionSummary>

                        <AccordionDetails sx={{ px: 0.7, pt: 0.2, pb: 1.2 }}>
                            {/*
                                Vista anterior por franjas horarias.
                                La dejamos comentada por si mas adelante se vuelve a ordenar el calendario por horas.

                                <DetalleDia fechaSeleccionada={dayId} eventos={eventosDia} embedded compact />
                            */}
                            <Box
                                sx={{
                                    borderRadius: '8px',
                                    border: '1px solid rgba(0,0,0,0.12)',
                                    backgroundColor: '#fff',
                                    p: 1,
                                }}
                            >
                                {eventosDia.length === 0 ? (
                                    <Box
                                        sx={{
                                            borderRadius: '8px',
                                            border: '1px dashed rgba(0,0,0,0.18)',
                                            minHeight: 64,
                                            display: 'grid',
                                            placeItems: 'center',
                                            textAlign: 'center',
                                            px: 2,
                                            color: 'text.secondary',
                                        }}
                                    >
                                        <Typography sx={{ fontSize: 13 }}>
                                            Sin tareas planificadas
                                        </Typography>
                                    </Box>
                                ) : (
                                    <Stack spacing={1}>
                                        {eventosDia.map((evento) => (
                                            <TarjetaTarea
                                                key={`${evento.tipo_calendario}-${evento.id}`}
                                                id={`${evento.tipo_calendario}-${evento.id}`}
                                                tarea={evento}
                                                onChanged={onChanged}
                                            />
                                        ))}
                                    </Stack>
                                )}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Stack>
    );
};
